import { POSITION_APPROXIMATE_ABOVE_M, POSITION_INTERVAL_SECONDS } from '@monrdv/shared';
import { apiFetch } from './api.js';
import { getMeta, setMeta } from './localdb.js';

/**
 * Position.
 *
 * Everything here is a limit rather than a feature, and each limit is in `FIELD.md` for a
 * reason worth keeping in front of whoever changes this file:
 *
 *   - **On shift only.** Between the first *en route* of the day and the last *done*. Never
 *     overnight, never on a day off.
 *   - **Coarse on purpose.** It costs battery to be precise about which street a van is on,
 *     and dispatch only needs to know who is nearest. The one precise fix is the one stamped
 *     at *arrived*, and that one travels inside the transition rather than as a breadcrumb.
 *   - **Never queued.** A breadcrumb is sent live or discarded. A position from an hour ago
 *     arriving late is worse than none, and a queue of them is the movement history this
 *     design refuses to build.
 *   - **Always visible.** A persistent indicator, exactly like the sync state. A technician
 *     can always see that it is on.
 */

export type Fix = { latitude: number; longitude: number; accuracyM: number | null; at: string };

export const isApproximate = (accuracyM: number | null): boolean =>
  accuracyM === null || accuracyM > POSITION_APPROXIMATE_ABOVE_M;

/**
 * One fix, now.
 *
 * Used at *arrived*, where precision is worth the battery: this is what proves attendance. It
 * resolves to null rather than throwing, because a phone in a mechanical room gets no fix at
 * all and the work still happened — self-reported *arrived* remains the record of record.
 */
export function currentFix(timeoutMs = 10_000): Promise<Fix | null> {
  if (!('geolocation' in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyM: Number.isFinite(position.coords.accuracy) ? Math.round(position.coords.accuracy) : null,
          at: new Date(position.timestamp).toISOString(),
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

let timer: number | undefined;
let watching = false;

/** Whether breadcrumbs are being sent right now. The indicator reads this. */
export const isReporting = (): boolean => watching;

/**
 * Start reporting, if the business has turned it on and the technician is on shift.
 *
 * The shift is marked by the client at the first *en route* and cleared at the last *done*, so
 * the phone is not reporting from a driveway at eight in the evening.
 */
export async function startReporting(enabled: boolean): Promise<void> {
  if (!enabled || watching || !('geolocation' in navigator)) return;
  const shift = await getMeta('shiftStartedAt');
  if (!shift) return;

  watching = true;
  const send = async () => {
    // Low accuracy deliberately, and a generous maximumAge: this only has to be good enough
    // to say who is nearest.
    navigator.geolocation.getCurrentPosition(
      (position) => {
        void apiFetch('/positions', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyM: Number.isFinite(position.coords.accuracy) ? Math.round(position.coords.accuracy) : null,
          }),
          // Fire and forget. There is nothing to retry: the next one is two minutes away and
          // will be better than this one.
        }).catch(() => {});
      },
      () => {},
      { enableHighAccuracy: false, timeout: 20_000, maximumAge: POSITION_INTERVAL_SECONDS * 1000 },
    );
  };

  void send();
  timer = window.setInterval(() => void send(), POSITION_INTERVAL_SECONDS * 1000);
}

/** Stop reporting, and tell the server to forget where this technician was. */
export async function stopReporting(): Promise<void> {
  watching = false;
  if (timer) window.clearInterval(timer);
  timer = undefined;
  await apiFetch('/positions/me', { method: 'DELETE' }).catch(() => {});
}

/** The shift begins at the first *en route* of the day. */
export async function beginShift(): Promise<void> {
  if (!(await getMeta('shiftStartedAt'))) await setMeta('shiftStartedAt', new Date().toISOString());
}

/** …and ends when nothing is left running. */
export async function endShift(): Promise<void> {
  await setMeta('shiftStartedAt', '');
  await stopReporting();
}
